/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getAnnouncementFeeds, markAnnouncementsAsRead, getAttachmentDownloadUrl } from '../../services/Feed.service';
import {
  transformApiAnnouncementToDisplay,
  transformSocketEventToAnnouncement,
  checkUserRoleType,
  isAnnouncementUnread,
  stripNewTags,
  updatePendingMarkAsReadAnnouncements,
} from '../../mappers/feedUtility';
import { FEED_TYPE, PAGINATION_CONSTANTS } from '../../constants';
import { ROLES } from '../../../../../constants';
import { detectFileType } from '@orijinworks/frontend-commons';

/**
 * Hook for managing Announcements functionality
 * Handles announcement listing, type filtering, and pagination
 * Socket events are filtered based on the selected type
 */
export const useAnnouncementsHook = (isActive = false, decrementUnreadCount, incrementUnreadCount) => {
  // Get user details from Redux store
  const { userDetail } = useSelector((state) => state.app || {});
  const currentUserKey = userDetail?.userId;

  // State
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('My Announcement');
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const selectedTypeRef = useRef(selectedType); // Ref to track current selectedType
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const pendingMarkAsReadRef = useRef([]); // Track announcements to mark as read when navigating away

  /**
   * Map UI type values to API type values
   * UI uses singular form, API uses uppercase with underscore
   */
  const mapTypeToApiValue = useCallback((uiType) => {
    switch (uiType) {
      case 'All Announcement':
        return 'ALL_ANNOUNCEMENTS';
      case 'My Announcement':
        return 'MY_ANNOUNCEMENTS';
      default:
        return 'ALL_ANNOUNCEMENTS';
    }
  }, []);

  /**
   * Transform announcement data with event handlers
   */
  const transformAnnouncementWithHandlers = useCallback((announcement) => {
    return transformApiAnnouncementToDisplay(announcement);
  }, []);

  /**
   * Update announcements state based on reset flag
   */
  const updateAnnouncementsState = useCallback((newAnnouncements, shouldReset) => {
    setAnnouncements((prev) => shouldReset ? newAnnouncements : [...prev, ...newAnnouncements]);
  }, []);

  /**
   * Reset announcements state to empty
   */
  const resetAnnouncementsState = useCallback(() => {
    setAnnouncements([]);
    setTotalCount(0);
    setHasMore(false);
  }, []);

  /**
   * Process announcements for ALL_ANNOUNCEMENTS type
   * Removes 'New' tags and marks as read for display
   */
  const processAllAnnouncements = useCallback((announcements) => {
    return announcements.map((announcement) => ({
      ...announcement,
      isNew: false,
      tags: stripNewTags(announcement.tags || []),
    }));
  }, []);

  /**
   * Filter announcements that need to be marked as read
   * Only includes announcements with readAt explicitly set to null
   */
  const filterUnreadAnnouncements = useCallback((announcements) => {
    return announcements
      .filter((announcement) => isAnnouncementUnread(announcement))
      .map((announcement) => announcement.id)
      .filter((id) => id);
  }, []);

  /**
   * Handle decrement of unread count
   * Safely calls the callback if provided
   */
  const handleUnreadCountDecrement = useCallback((count) => {
    if (typeof decrementUnreadCount === 'function') {
      try {
        decrementUnreadCount(FEED_TYPE.ANNOUNCEMENT, count);
      } catch {
        // Ignore callback errors so announcement state updates are not interrupted.
      }
    }
  }, [decrementUnreadCount]);

  /**
   * Handle increment of unread count
   * Safely calls the callback if provided
   */
  const handleUnreadCountIncrement = useCallback(() => {
    if (typeof incrementUnreadCount === 'function') {
      try {
        incrementUnreadCount(FEED_TYPE.ANNOUNCEMENT);
      } catch {
        // Ignore callback errors so announcement state updates are not interrupted.
      }
    }
  }, [incrementUnreadCount]);

  /**
   * Update announcement state after marking as read
   */
  const updateAnnouncementsAfterMarkAsRead = useCallback((markedIds) => {
    handleUnreadCountDecrement(markedIds.length);
    setAnnouncements((prevAnnouncements) => {
      const idsSet = new Set(markedIds);
      let didChange = false;

      const updatedAnnouncements = prevAnnouncements.map((announcement) => {
        if (!announcement || !idsSet.has(announcement.id)) {
          return announcement;
        }

        const cleanedTags = stripNewTags(announcement.tags || []);
        const shouldUpdate = announcement.isNew || cleanedTags.length !== (announcement.tags || []).length;

        if (!shouldUpdate) {
          return announcement;
        }

        didChange = true;
        return {
          ...announcement,
          isNew: false,
          tags: cleanedTags,
          readAt: announcement.readAt || new Date().toISOString(),
        };
      });

      return didChange ? updatedAnnouncements : prevAnnouncements;
    });
  }, [handleUnreadCountDecrement]);

  /**
   * Mark announcements as read via API
   */
  const markAnnouncementsAsReadAsync = useCallback(async (content) => {
    const announcementIdsToMark = filterUnreadAnnouncements(content);

    if (announcementIdsToMark.length === 0) {
      return;
    }

    try {
      await markAnnouncementsAsRead(announcementIdsToMark);
      updateAnnouncementsAfterMarkAsRead(announcementIdsToMark);
    } catch (error) {
      throw error;
    }
  }, [filterUnreadAnnouncements, updateAnnouncementsAfterMarkAsRead]);

  /**
   * Load announcements from API
   */
  const loadAnnouncements = useCallback(
    async (type, page, shouldReset, size = PAGINATION_CONSTANTS.DEFAULT_ANNOUNCEMENT_PAGE_SIZE) => {
      setIsLoading(true);
      try {
        const apiType = mapTypeToApiValue(type);
        const response = await getAnnouncementFeeds({
          type: apiType,
          page: page,
          size: size,
        });

        const { content = [], totalElements = 0, hasMore: responseHasMore = false } = response.data || {};

        // Transform announcements
        let transformedAnnouncements = content.map(transformAnnouncementWithHandlers);

        // Process based on announcement type
        if (apiType === 'ALL_ANNOUNCEMENTS') {
          transformedAnnouncements = processAllAnnouncements(transformedAnnouncements);
        }

        // Update state
        updateAnnouncementsState(transformedAnnouncements, shouldReset);
        setTotalCount(totalElements);
        setHasLoadedInitial(true);
        setHasMore(responseHasMore);

        // Track unread announcements to mark as read when navigating away
        // Keep "New" badges visible while user is on the page
        if (apiType === 'MY_ANNOUNCEMENTS') {
          pendingMarkAsReadRef.current = updatePendingMarkAsReadAnnouncements(
            pendingMarkAsReadRef.current,
            transformedAnnouncements,
            shouldReset
          );
        }
      } catch (error) {
        if (shouldReset) {
          resetAnnouncementsState();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      mapTypeToApiValue,
      transformAnnouncementWithHandlers,
      processAllAnnouncements,
      updateAnnouncementsState,
      resetAnnouncementsState,
      markAnnouncementsAsReadAsync,
    ]
  );


  /**
   * Handle type change (All Announcements / My Announcements)
   */
  const handleTypeChange = useCallback(
    (newType) => {
      setIsLoading(true);
      setAnnouncements([]);
      setSelectedType(newType);
      setCurrentPage(1);
      loadAnnouncements(newType, 1, true, 10);
    },
    [loadAnnouncements]
  );

  /**
   * Handle load more pagination
   */
  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadAnnouncements(selectedType, nextPage, false, 10);
    }
  }, [hasMore, isLoading, currentPage, selectedType, loadAnnouncements]);

  /**
   * Load announcements when tab becomes active or type changes
   * Uses a single effect with proper guards to prevent duplicate API calls:
   * 1. Only loads when tab is active
   * 2. Uses existing isLoading state to prevent concurrent calls
   * 3. Resets page when switching tabs or changing type
   * 4. Doesn't include loadAnnouncements in deps to prevent unnecessary re-runs
   *    (loadAnnouncements is stable enough via useCallback)
   */
  useEffect(() => {
    // Only proceed if tab is active and not currently loading
    if (!isActive || isLoading) {
      return;
    }

    // Reset page and load announcements
    setCurrentPage(1);
    loadAnnouncements(selectedType, 1, true, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, selectedType]); // Only depend on isActive and selectedType

  /**
   * Mark announcements as read when user navigates away from the page
   * This keeps "New" badges visible while the user is on the page
   */
  useEffect(() => {
    // When navigating away (isActive becomes false), mark all pending announcements as read
    if (!isActive && pendingMarkAsReadRef.current.length > 0) {
      const announcementsToMark = [...pendingMarkAsReadRef.current];
      pendingMarkAsReadRef.current = []; // Clear the ref
      markAnnouncementsAsReadAsync(announcementsToMark);
    }
  }, [isActive, markAnnouncementsAsReadAsync]);

  /**
   * Expose refetch function to reload announcements
   * Can be called after creating an announcement
   * Uses ref to always get the latest selectedType value
   */
  const refetchAnnouncements = useCallback(() => {
    const currentType = selectedTypeRef.current;
    loadAnnouncements(currentType, 1, true, 10);
  }, [loadAnnouncements]);


    /**
   * Keep ref in sync with selectedType state
   */
    useEffect(() => {
      selectedTypeRef.current = selectedType;
    }, [selectedType]);

  /**
   * Handle new announcement feed creation from socket event
   * Filters announcements based on selected type (All/My Announcements)
   * "My Announcement" includes announcements where user is sender OR member
   * Shows "New" tag momentarily and marks as read immediately via API
   */
  const onAnnouncementCreated = useCallback((eventData) => {

    // 🚨 Exit if user not identified
    if (!currentUserKey) {
      return;
    }

    // 🧑‍💻 Identify sender and members
    const senderId = String(eventData.sender_id);
    const members = eventData.members_info || [];
    const isSender = senderId === String(currentUserKey);
    const isMember = members.some(m => String(m.id) === String(currentUserKey));
    const isMyAnnouncement = isSender || isMember;

    // 🔔 Increment unread count for recipients only
    if (isMember && !isSender) handleUnreadCountIncrement();

    // 🧭 Filter based on selected tab
    const isViewingMyAnnouncements = selectedType === 'My Announcement';
    const isViewingAllAnnouncements = selectedType === 'All Announcement';

    if (isViewingMyAnnouncements && !isMyAnnouncement) {
      return;
    }

    if (isViewingAllAnnouncements && isMyAnnouncement) {
      return;
    }

    // 🧱 Transform event data
    let announcement = transformSocketEventToAnnouncement(eventData);

    // 🧽 Remove "New" tag if:
    //    - Viewing All Announcements (always), OR
    //    - Viewing My Announcements AND user is the sender
    if (isViewingAllAnnouncements || (isViewingMyAnnouncements && isSender)) {
      announcement = {
        ...announcement,
        isNew: false,
        tags: stripNewTags(announcement.tags || []),
      };
    }

    // 📥 Add to state (prepend new announcement)
    setAnnouncements(prev => [announcement, ...prev]);
    setTotalCount(prev => prev + 1);

    // ✅ Track announcement to mark as read when navigating away (keeps "New" badge visible while on page)
    if (isViewingMyAnnouncements && !isSender && announcement.id && isAnnouncementUnread(announcement)) {
      pendingMarkAsReadRef.current = [...pendingMarkAsReadRef.current, announcement];
    }

  }, [selectedType, currentUserKey, handleUnreadCountIncrement]);

  /**
   * Handle new message in announcement feed from socket event
   */
  const onAnnouncementMessageReceived = useCallback((eventData) => {
    
    // For announcement messages, we could either:
    // 1. Refetch the entire announcement to get updated message count
    // 2. Update the existing announcement in the list with new message info
    
    // For now, let's refetch to ensure data consistency
    refetchAnnouncements();
  }, [refetchAnnouncements]);

  /**
   * Check if user has permission to create announcements
   */
  const showNewAnnouncementButton = useMemo(() => {
    return checkUserRoleType([ROLES.SUPER_ADMIN, ROLES.STAFF, ROLES.MESSAGING_ONLY, ROLES.TEACHER]);
  }, []);

  const onAnnouncementDeleteHandler = useCallback((announcementId) => {
    setAnnouncements((prev) => prev.filter((announcement) => announcement.id !== announcementId));
    setTotalCount((prev) => prev - 1);
  }, []);

  const fetchPreview = useCallback(async (attachment) => {
    const attachmentId = attachment.id ?? attachment.attachmentId;
    if (!attachmentId) throw new Error('Attachment id is required');
    const response = await getAttachmentDownloadUrl(attachmentId);
    const url = response?.data?.presignedUrl ?? response?.data?.url ?? response?.data?.downloadUrl;
    if (!url) throw new Error('URL not found in response');
    return {
      url,
      fileName: attachment.filename ?? '',
      fileType: detectFileType(attachment.filename || attachment.mimeType || ''),
    };
  }, []);
  /**
   * Return object matching the frontend commons API structure
   */
  return {
    announcements, // Direct list (no filtering needed since no search)
    selectedType,
    isLoading,
    hasLoadedInitial,
    showLoadMore: hasMore && !isLoading,
    showNewAnnouncementButton,
    allAnnouncementText: 'All Announcement',
    myAnnouncementText: 'My Announcement',
    totalAnnouncementCount: totalCount,
    onAnnouncementTypeChangeHandler: handleTypeChange,
    onLoadMore: handleLoadMore,
    refetchAnnouncements,
    // Socket event handlers
    onAnnouncementCreated,
    onAnnouncementMessageReceived,
    onAnnouncementDeleteHandler,
    fetchPreview,
  };
};
